import {
  booleanAttribute,
  Directive,
  effect,
  ElementRef,
  inject,
  input
} from '@angular/core';
import { ComponentPortal } from '@angular/cdk/portal';
import { Overlay } from '@angular/cdk/overlay';

import { BLOCKABLE } from '../../domain/blockable.interface';
import { BlockingComponent } from './blocking.component';

import type { InputSignal, InputSignalWithTransform } from '@angular/core';
import type { OverlayRef } from '@angular/cdk/overlay';
import type { Blockable } from '../../domain/blockable.interface';

export type BlockTypes = 'spinningWheel' | 'blockScreen' | 'spinningAndBlock';
/**
 * BlockDirective
 * Attribute directive to control the host element's disabled behavior.
 *
 * When a consumer disables the element, it can specify behaviors
 * for an element, such as a spinning wheel on a button or blocking
 * the entire screen for buy/sell page submit
 *
 * Attention: The host element must implement the Blockable interface
 */
@Directive({
  selector: '[tdWbBlock]',
  exportAs: 'tdWbBlock',
  host: {
    '[attr.disabled]': 'disabled() ? "" : null'
  }
})
export class BlockDirective {
  tdWbBlock: InputSignal<BlockTypes> = input.required<BlockTypes>();
  disabled: InputSignalWithTransform<boolean, unknown> = input(false, { transform: booleanAttribute });

  private overlay: Overlay = inject(Overlay);
  private el: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private blockable: Blockable | null = inject(BLOCKABLE, { self: true, optional: true });

  private overlayRef?: OverlayRef;

  constructor() {
    effect((onCleanup: (cleanupFn: () => void) => void): void => {
      const disabled = this.disabled();
      const type = this.tdWbBlock();

      if (disabled) {
        this.block(type);
        onCleanup((): void => {
          this.unblock(type);
        });
      }
    });
  }

  private block(type: BlockTypes): void {
    switch (type) {
      case 'spinningWheel':
        this.blockable?.setBlocked(true);
        break;
      case 'blockScreen':
        this.addBlockingElement();
        break;
      case 'spinningAndBlock':
        this.addBlockingElement();
        this.blockable?.setBlocked(true);
        break;
    }
  }

  private unblock(type: BlockTypes): void {
    const isBlocked = this.blockable?.isBlocked() ?? false;
    switch (type) {
      case 'spinningWheel':
        this.blockable?.setBlocked(false);
        break;
      case 'blockScreen':
        this.removeBlockingElement();
        break;
      case 'spinningAndBlock':
        this.blockable?.setBlocked(false);
        this.removeBlockingElement();
        break;
    }
    if (isBlocked) {
      Promise.resolve().then((): void => {
        this.el.nativeElement.focus();
      });
    }
  }

  private addBlockingElement(): void {
    this.overlayRef = this.overlay.create();
    this.overlayRef.attach(new ComponentPortal(BlockingComponent));
  }

  private removeBlockingElement(): void {
    if (this.overlayRef !== undefined) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
  }
}
